// NeedsCheckStep.tsx — the onboarding "needs" step (survivors only, after reminders,
// before entry): a short, one-question-per-screen check that ends in a plain list of
// where ReNurture can help. It is a signpost, not an assessment.
//
// It is dressed to match the pre-sign-in intro run (IntroJourney): the same cream paper
// gradient, the same soft watercolour wash arriving slowly under multiply, the serif
// heading with an amber italic accent, and the same tiny ink dots for progress.
//
// ANSWERS ARE NEVER STORED. They live in this component's state only, long enough to build
// the reveal; they are never written to progress, localStorage, the network or telemetry.
// Only the THEMES the reveal surfaced (fear/obligation/.../ex) are handed up via onThemes, so
// Progress can show "What we're working on together". Renders inside OnboardingV2's Frame, like RemindersStep.
import React from 'react';
import { Display, Body, Btn } from '@/components/ui';
import { Icon } from '@/components/icons';
import {
  NEEDS_ITEMS, SCALE, EX_OPTIONS, EX_QUESTION, FALLBACK_REVEAL, QUESTION_EYEBROW, revealLines, revealSummary, startOrder,
  type NeedsKey, type ExAnswer, type StartKey,
} from './needsCheck';

const ADVANCE_MS = 250;
const TOTAL = NEEDS_ITEMS.length + 1;
const UI = 'Inter, system-ui, -apple-system, sans-serif';
const IMG = '/newbuttonimages/';
const EASE = 'cubic-bezier(0.22,0.61,0.36,1)';

// Slightly deeper than --ink-soft: the watercolour wash sits behind this step's text,
// and plain --ink-soft dips just under 4.5:1 over its darkest strokes (measured 2026-10-05).
const INK_ON_WASH = '#4a463e';

const quietLink: React.CSSProperties = {
  background: 'none', border: 0, cursor: 'pointer', font: 'inherit', fontSize: 13.5,
  color: INK_ON_WASH, textDecoration: 'underline', textUnderlineOffset: 2,
  minHeight: 44, padding: '6px 12px', textAlign: 'center', lineHeight: 1.4,
};

const START_LABEL: Record<StartKey, string> = { lesson: 'Start my first lesson', settle: 'Settle my body first · 2 min' };

const rise = (delay = 0): React.CSSProperties => ({ animation: `fadeUp 700ms ${EASE} both`, animationDelay: `${delay}ms` });

/**
 * The intro run's paper and wash. It bleeds over Frame's padding (negative margins) so the
 * gradient is full-bleed, then puts the padding back inside. The wash fades up slowly once
 * decoded and is composited with `multiply` so the artwork's white leaves the paper alone
 * (same technique and positioning as IntroJourney's SoftImage).
 */
function Paper({ image, opacity, children }: { image: string; opacity: number; children: React.ReactNode }) {
  const [ready, setReady] = React.useState(false);
  return (
    <div
      style={{
        position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        margin: 'calc(-26px - var(--safe-top)) -22px calc(-26px - var(--safe-bottom))',
        padding: 'calc(26px + var(--safe-top)) 26px calc(26px + var(--safe-bottom))',
        background: 'linear-gradient(178deg, #fbf6ed 0%, var(--cream, #f7f1e6) 42%, #f2e9d9 100%)',
        color: 'var(--ink, #2c2a25)',
      }}
    >
      <img
        src={IMG + image}
        alt=""
        aria-hidden="true"
        decoding="async"
        onLoad={() => setReady(true)}
        onError={() => setReady(true)}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: '78% 50%',
          mixBlendMode: 'multiply', pointerEvents: 'none',
          opacity: ready ? opacity : 0, transition: `opacity 1800ms ${EASE}`,
        }}
      />
      <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>{children}</div>
    </div>
  );
}

/** The intro run's progress: tiny ink dots, current one darker, no counter. */
function Dots({ at }: { at: number }) {
  return (
    <div role="img" aria-label={`Question ${at} of ${TOTAL}`} style={{ display: 'flex', gap: 6, justifyContent: 'center', margin: '8px 0 6px' }}>
      {Array.from({ length: TOTAL }, (_, i) => i + 1).map((n) => (
        <span key={n} style={{ width: 5, height: 5, borderRadius: 999, background: 'var(--ink, #2c2a25)', opacity: n === at ? 0.45 : 0.14, transition: 'opacity 400ms ease' }} />
      ))}
    </div>
  );
}

function Back({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-label="Back" style={{ width: 40, height: 40, borderRadius: 999, border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
      <Icon name="chevLeft" size={18} />
    </button>
  );
}

const hidden: React.CSSProperties = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' };

export function NeedsCheckStep({
  firstName,
  onStart,
  onSkip,
  onThemes,
  onFullCheck,
}: {
  firstName?: string;
  onStart: (choice: 'lesson' | 'settle' | 'explore') => void;
  onSkip: () => void;
  /** Called each time the reveal shows, with the surfaced theme keys (empty = fallback reveal). */
  onThemes?: (themes: string[]) => void;
  /** The reveal's "Take the full check" link; omitted = link hidden (FOGS_FULL_CHECK off). */
  onFullCheck?: () => void;
}) {
  // 0 = lead-in, 1..6 = questions, 7 = reveal
  const [screen, setScreen] = React.useState(0);
  const [scale, setScale] = React.useState<Partial<Record<NeedsKey, number>>>({});
  const [ex, setEx] = React.useState<ExAnswer | undefined>(undefined);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  React.useEffect(() => clearTimer, []);
  // Move focus to the heading whenever the screen changes so keyboard and screen-reader users
  // hear the new question and can't re-answer it with a stray Enter on a reused button.
  const headRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => { if (screen > 0) headRef.current?.focus(); }, [screen]);

  // Hand up the themes (never the answers) when the reveal renders. Latest answers via a ref so
  // the effect fires once per arrival at the reveal, not per keystroke.
  const themesRef = React.useRef<string[]>([]);
  themesRef.current = revealLines({ scale, ex }).map((l) => l.key);
  const onThemesRef = React.useRef(onThemes);
  onThemesRef.current = onThemes;
  const atReveal = screen > TOTAL;
  React.useEffect(() => { if (atReveal) onThemesRef.current?.(themesRef.current); }, [atReveal]);

  const go = (n: number) => { clearTimer(); setScreen(n); };
  const name = (firstName || '').trim();

  if (screen === 0) {
    return (
      <Paper image="today_steadyyourselfbox.png" opacity={0.25}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          <Display parts={{ pre: '', em: 'Welcome', post: name ? `, ${name}.` : '.' }} size={30} weight={300} lh={1.2} as="h1" style={{ letterSpacing: '0.005em', ...rise() }} />
          <Body size={16} style={{ lineHeight: 1.6, color: INK_ON_WASH, maxWidth: '30ch', marginTop: 20, ...rise(180) }}>
            Before your first lesson, let’s find out a little about what you need, and how ReNurture can help.
          </Body>
          <Body size={14} style={{ lineHeight: 1.6, color: 'var(--ink-soft)', maxWidth: '34ch', marginTop: 14, ...rise(330) }}>
            Answer for how things were, not how you’ve learned to explain them. Even ‘sometimes’ counts.
          </Body>
          <div style={{ width: '100%', marginTop: 30, ...rise(480) }}>
            <Btn full size="lg" onClick={() => go(1)}>Let’s go</Btn>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
              <button type="button" onClick={onSkip} style={quietLink}>Skip for now</button>
            </div>
          </div>
        </div>
      </Paper>
    );
  }

  if (screen <= TOTAL) {
    const idx = screen - 1;
    const isEx = idx === NEEDS_ITEMS.length;
    const item = isEx ? null : NEEDS_ITEMS[idx];
    const question = isEx ? EX_QUESTION : item!.question;
    const options: { value: number | ExAnswer; label: string }[] = isEx ? EX_OPTIONS : SCALE;
    const selected: number | ExAnswer | undefined = isEx ? ex : scale[item!.key];
    const labelId = `needs-q-${screen}`;

    const choose = (value: number | ExAnswer) => {
      clearTimer();
      if (isEx) setEx(value as ExAnswer);
      else setScale((s) => ({ ...s, [item!.key]: value as number }));
      if (selected === value) { go(screen + 1); return; }
      timer.current = setTimeout(() => { timer.current = null; setScreen(screen + 1); }, ADVANCE_MS);
    };

    return (
      <Paper image="today_steadyyourselfbox.png" opacity={0.14}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Back onClick={() => go(screen - 1)} />
          <button type="button" onClick={onSkip} style={quietLink}>Skip</button>
        </div>
        <div key={screen} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '10px 0 4px' }}>
          <div ref={headRef} tabIndex={-1} style={{ outline: 'none', textAlign: 'center', marginBottom: 20, ...rise() }}>
            {!isEx && (
              <p style={{ margin: '0 0 10px', fontFamily: UI, fontSize: 14, letterSpacing: '0.05em', color: 'var(--ink-soft, #5a554c)' }}>{QUESTION_EYEBROW}</p>
            )}
            <Display text={question} size={20} weight={400} lh={1.32} as="h2" />
          </div>
          <div role="radiogroup" aria-labelledby={labelId} style={{ display: 'flex', flexDirection: 'column', gap: 9, ...rise(140) }}>
            <span id={labelId} style={hidden}>{question}</span>
            {options.map((o) => {
              const on = selected === o.value;
              return (
                <button
                  key={String(o.value)}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => choose(o.value)}
                  style={{
                    width: '100%', minHeight: 48, padding: '12px 18px', borderRadius: 999, cursor: 'pointer',
                    border: on ? '1px solid var(--green-deep)' : '1px solid var(--hair-strong)',
                    background: on ? 'rgba(29,158,117,0.10)' : 'var(--card-top)',
                    color: on ? 'var(--green-deep)' : 'var(--ink)',
                    fontFamily: UI, fontSize: 15, fontWeight: on ? 700 : 500,
                  }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
        <Dots at={screen} />
      </Paper>
    );
  }

  const answers = { scale, ex };
  const lines = revealLines(answers);
  const summary = revealSummary(answers);
  const [first, second] = startOrder(answers);
  return (
    <Paper image="triage_feeling-unsafe-overlay.png" opacity={0.14}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Back onClick={() => go(TOTAL)} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '8px 0 4px' }}>
        <div ref={headRef} tabIndex={-1} style={{ outline: 'none', textAlign: 'center', marginBottom: 16, ...rise() }}>
          <Display parts={{ pre: 'Your emotional ', em: 'needs', post: '.' }} size={25} weight={300} lh={1.22} as="h2" style={{ letterSpacing: '0.005em' }} />
        </div>
        {lines.length > 0 ? (
          <>
            <Body size={14} style={{ lineHeight: 1.5, color: 'var(--ink-soft)', textAlign: 'center', margin: '-6px auto 12px', maxWidth: '32ch', ...rise(80) }}>From your answers, these stood out. Here’s how ReNurture helps with each.</Body>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, ...rise(160) }}>
              {lines.map((l, n) => (
                <li key={l.key} style={{ display: 'flex', gap: 14, alignItems: 'baseline', padding: '7px 0', borderTop: n === 0 ? 'none' : '1px solid var(--hair)' }}>
                  <span style={{ flex: '0 0 84px', fontFamily: UI, fontSize: 13, fontWeight: 700, lineHeight: 1.35, color: 'var(--ink)' }}>{l.label}</span>
                  <span style={{ flex: 1, fontFamily: UI, fontSize: 14, lineHeight: 1.45, color: 'var(--ink-soft)' }}>{l.text}</span>
                </li>
              ))}
            </ul>
            <Body size={13.5} style={{ lineHeight: 1.55, color: 'var(--ink-soft)', marginTop: 12, ...rise(300) }}>{summary}</Body>
          </>
        ) : (
          <Body size={15.5} style={{ lineHeight: 1.65, color: 'var(--ink-soft)', maxWidth: '34ch', textAlign: 'center', margin: '0 auto', ...rise(160) }}>{FALLBACK_REVEAL}</Body>
        )}
      </div>
      <div style={{ marginTop: 10 }}>
        <Btn full size="lg" onClick={() => onStart(first)}>{START_LABEL[first]}</Btn>
        <Btn full variant="soft" onClick={() => onStart(second)} style={{ marginTop: 8, minHeight: 48, fontSize: 15 }}>{START_LABEL[second]}</Btn>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 2 }}>
          <button type="button" onClick={() => onStart('explore')} style={quietLink}>Look around first</button>
          {onFullCheck && <button type="button" onClick={onFullCheck} style={{ ...quietLink, minHeight: 44, marginTop: -4 }}>
            Take the full check (about 10 minutes)
          </button>}
        </div>
      </div>
    </Paper>
  );
}
